import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SearchInput } from "@/components/ui/search-input";
import { Phone } from "lucide-react";
import { useState } from "react";
import {
  FollowUpStatusBadge,
  PriorityDot,
  getPriorityFromDate,
} from "./FollowUpStatusBadge";

export function SponsorListSidebar({ sponsors = [], selectedId, onSelect }) {
  const [search, setSearch] = useState("");

  // Sort: overdue → today → upcoming → none
  const priorityOrder = { overdue: 1, today: 2, upcoming: 3, none: 4 };

  const sorted = [...sponsors].sort((a, b) => {
    const pa = getPriorityFromDate(a.followUp?.nextActionDate);
    const pb = getPriorityFromDate(b.followUp?.nextActionDate);
    return (priorityOrder[pa] || 4) - (priorityOrder[pb] || 4);
  });

  const filtered = search.trim()
    ? sorted.filter((s) => {
        const q = search.toLowerCase();
        return (
          s.sponsorName?.toLowerCase().includes(q) ||
          s.mahalluName?.toLowerCase().includes(q) ||
          s.place?.toLowerCase().includes(q) ||
          s.sponsorPhone?.includes(q)
        );
      })
    : sorted;

  if (sponsors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Phone className="h-10 w-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">No sponsors in queue</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-2 pb-2 pt-1">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search sponsors..."
        />
      </div>
      <ScrollArea className="flex-1">
        <div>
          {filtered.map((s) => {
            const followUp = s.followUp || {};
            const isSelected = selectedId === s._id;
            const subtext =
              s.type === "Mahallu" ? s.mahalluName || "" : s.place || "";

            return (
              <div
                key={s._id}
                onClick={() => onSelect(s)}
                className={cn(
                  "flex items-start gap-2 px-3 py-2.5 border-b cursor-pointer hover:bg-blue-50/40 transition-colors",
                  isSelected && "bg-blue-50 border-l-2 border-l-primary",
                )}
              >
                <PriorityDot
                  nextActionDate={followUp.nextActionDate}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">
                    {s.sponsorName}
                  </p>
                  {subtext && (
                    <p className="text-[10px] text-muted-foreground truncate">
                      {subtext}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-muted-foreground">
                    {followUp.nextActionDate
                      ? new Date(followUp.nextActionDate).toLocaleDateString(
                          "en-IN",
                          { day: "2-digit", month: "short" },
                        )
                      : "No date"}
                  </p>
                  <FollowUpStatusBadge status={followUp.status} />
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && search.trim() && (
            <p className="text-xs text-muted-foreground text-center py-8">
              No matching sponsors
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
